import React, { useCallback, useEffect, useState } from 'react';
import request from '../utils/request';

// Concierge Team applicants (public /apply flow on bellhopt.com).
// Read-only list + detail drawer. Mirrors GuestEngagementScreen patterns.

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'started', label: 'In progress' },
];

const STATUS_STYLES = {
  submitted: 'bg-emerald-50 text-emerald-700',
  started: 'bg-amber-50 text-amber-700',
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const AVAILABILITY_LABELS = {
  available: { label: '12-4 PM', cls: 'bg-emerald-50 text-emerald-700' },
  partial: { label: 'Partial', cls: 'bg-amber-50 text-amber-700' },
  unavailable: { label: 'Off', cls: 'bg-zinc-100 text-zinc-500' },
};

const VIDEO_QUESTIONS = [
  'Introduce yourself',
  'Hospitality experience',
  'Handling a problem',
  'Reliability',
];

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const fmtDay = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const fmtPhone = (e164) => {
  const digits = String(e164 || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return e164 || '—';
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const fmtDuration = (sec) => {
  if (!sec && sec !== 0) return '';
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
};

const Label = ({ children }) => (
  <p className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">{children}</p>
);

const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLES[status] || 'bg-zinc-100 text-zinc-600'}`}>
    {status === 'started' ? 'In progress' : status}
  </span>
);

const ImageCard = ({ label, url }) => (
  <div className="border border-zinc-200 rounded-lg overflow-hidden bg-[#FAFAFA]">
    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
      <span className="text-xs font-semibold text-zinc-600">{label}</span>
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline">
          Open
        </a>
      )}
    </div>
    {url ? (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={label} className="w-full max-h-56 object-contain" />
      </a>
    ) : (
      <div className="h-24 flex items-center justify-center text-xs text-zinc-400">Not available</div>
    )}
  </div>
);

/* ─── Detail drawer ─── */
const ApplicantDrawer = ({ applicantId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    request
      .get(`/admin/concierge-applicants/${applicantId}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.error || 'Failed to load applicant'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applicantId]);

  const a = data?.applicant;
  const age = ageFromDob(a?.dateOfBirth);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-zinc-900 truncate">
                {loading ? 'Loading…' : `${a?.firstName || ''} ${a?.lastName || ''}`.trim() || a?.email}
              </h2>
              {a && <StatusPill status={a.status} />}
            </div>
            {a?.email && (
              <a href={`mailto:${a.email}`} className="text-xs text-indigo-600 hover:underline">{a.email}</a>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-500">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-zinc-400">Loading applicant…</div>}
          {err && <div className="m-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{err}</div>}

          {a && (
            <>
              {/* Profile */}
              <div className="px-5 py-4 border-b border-zinc-100 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label>Phone</Label>
                    <a href={`tel:${a.phone}`} className="text-zinc-900 hover:underline">{fmtPhone(a.phone)}</a>
                  </div>
                  <div>
                    <Label>Applied</Label>
                    <p className="text-zinc-900">{fmtDate(a.createdAt)}</p>
                  </div>
                  <div>
                    <Label>Submitted</Label>
                    <p className="text-zinc-900">{a.submittedAt ? fmtDate(a.submittedAt) : 'Not yet'}</p>
                  </div>
                  <div>
                    <Label>Date of birth</Label>
                    <p className="text-zinc-900">{fmtDay(a.dateOfBirth)}{age != null ? ` (${age})` : ''}</p>
                  </div>
                  <div>
                    <Label>Driver's license</Label>
                    <p className="text-zinc-900 font-mono">•••• {a.licenseLast4 || '—'}</p>
                  </div>
                  <div>
                    <Label>Applicant ID</Label>
                    <p className="text-zinc-500 font-mono text-xs break-all">{a._id}</p>
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <p className="text-zinc-900">
                    {a.address?.line1 ? `${a.address.line1}, ` : ''}
                    {[a.address?.city, a.address?.state, a.address?.zip].filter(Boolean).join(', ') || '—'}
                  </p>
                </div>
              </div>

              {/* License photos */}
              <div className="px-5 py-4 border-b border-zinc-100">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">License photos</h3>
                <div className="grid grid-cols-2 gap-3">
                  <ImageCard label="Front" url={a.licenseFrontUrl} />
                  <ImageCard label="Back" url={a.licenseBackUrl} />
                </div>
              </div>

              {/* Standards */}
              <div className="px-5 py-4 border-b border-zinc-100">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Standards acknowledgment</h3>
                {data.acknowledgment ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="space-y-3">
                      <div>
                        <Label>Signed as</Label>
                        <p className="text-zinc-900">{data.acknowledgment.typedName}</p>
                      </div>
                      <div>
                        <Label>Signed at</Label>
                        <p className="text-zinc-900">{fmtDate(data.acknowledgment.acknowledgedAt)}</p>
                      </div>
                      <div>
                        <Label>Standards version</Label>
                        <p className="text-zinc-900">{data.acknowledgment.standardsVersion}</p>
                      </div>
                    </div>
                    <ImageCard label="Signature" url={data.acknowledgment.signatureUrl} />
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">Not signed yet</p>
                )}
              </div>

              {/* Videos */}
              <div className="px-5 py-4 border-b border-zinc-100">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">
                  Video answers ({(data.videos || []).filter((v) => v.status === 'uploaded').length}/4)
                </h3>
                <div className="space-y-3">
                  {VIDEO_QUESTIONS.map((title, idx) => {
                    const video = (data.videos || []).find((v) => v.questionIndex === idx);
                    return (
                      <div key={idx} className="border border-zinc-200 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                          <span className="text-xs font-semibold text-zinc-700">Q{idx + 1} · {title}</span>
                          <span className="text-[11px] text-zinc-400">
                            {video?.status === 'uploaded'
                              ? [fmtDuration(video.durationSec), fmtDate(video.uploadedAt)].filter(Boolean).join(' · ')
                              : 'Not recorded'}
                          </span>
                        </div>
                        {video?.url ? (
                          // eslint-disable-next-line jsx-a11y/media-has-caption
                          <video src={video.url} controls preload="metadata" className="w-full max-h-72 bg-black" />
                        ) : (
                          <div className="h-16 flex items-center justify-center text-xs text-zinc-400">No video</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Availability */}
              <div className="px-5 py-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Availability (12-4 PM window)</h3>
                {data.availability ? (
                  <div className="grid grid-cols-7 gap-1.5">
                    {DAYS.map((day) => {
                      const entry = data.availability[day];
                      const meta = AVAILABILITY_LABELS[entry?.value] || { label: '—', cls: 'bg-zinc-50 text-zinc-400' };
                      return (
                        <div key={day} className="text-center" title={entry?.note || ''}>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{day.slice(0, 3)}</p>
                          <span className={`block px-1 py-1.5 rounded text-[11px] font-semibold ${meta.cls}`}>{meta.label}</span>
                          {entry?.note && <p className="text-[10px] text-zinc-500 mt-1 truncate">{entry.note}</p>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">Not submitted yet</p>
                )}
              </div>

              <p className="px-5 pb-5 text-[11px] text-zinc-400">
                Photo and video links expire after {Math.round((data.linkExpiresInSec || 3600) / 60)} minutes. Reopen the applicant to refresh them.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ─── Main screen ─── */
const ConciergeApplicantsScreen = () => {
  const [applicants, setApplicants] = useState([]);
  const [counts, setCounts] = useState({ started: 0, submitted: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ q: '', status: '', sort: '-createdAt', limit: 50, skip: 0 });

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v != null) params.set(k, String(v));
      });
      const { data } = await request.get(`/admin/concierge-applicants?${params.toString()}`);
      setApplicants(data.applicants || []);
      setTotal(data.total || 0);
      setCounts(data.counts || { started: 0, submitted: 0 });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load applicants');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchApplicants(); }, [fetchApplicants]);

  const updateFilter = (patch) => setFilters((f) => ({ ...f, ...patch, skip: 0 }));

  return (
    <div className="flex-1 overflow-auto bg-zinc-50 p-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Concierge Applicants</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {counts.submitted.toLocaleString()} submitted · {counts.started.toLocaleString()} in progress
            </p>
          </div>
          <button
            onClick={fetchApplicants}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
          >
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          <input
            type="text"
            placeholder="Search name, email, or phone"
            value={filters.q}
            onChange={(e) => updateFilter({ q: e.target.value })}
            className="col-span-2 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={filters.status}
            onChange={(e) => updateFilter({ status: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={filters.sort}
            onChange={(e) => updateFilter({ sort: e.target.value })}
            className="px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white"
          >
            <option value="-createdAt">Sort: Newest first</option>
            <option value="createdAt">Sort: Oldest first</option>
            <option value="-submittedAt">Sort: Recently submitted</option>
            <option value="lastName">Sort: Last name A–Z</option>
          </select>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Applicant</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Location</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-zinc-600">Videos</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Applied</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12"><div className="w-6 h-6 mx-auto border-2 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" /></td></tr>
                ) : applicants.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-zinc-400">No applicants match these filters</td></tr>
                ) : applicants.map((a) => (
                  <tr
                    key={a._id}
                    onClick={() => setSelectedId(a._id)}
                    className="hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-zinc-900">{`${a.firstName} ${a.lastName}`.trim() || a.email}</p>
                      <p className="text-xs text-zinc-500">{a.email}</p>
                    </td>
                    <td className="px-4 py-3 text-zinc-600 text-xs">{fmtPhone(a.phone)}</td>
                    <td className="px-4 py-3 text-zinc-600 text-xs">
                      {[a.address?.city, a.address?.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3"><StatusPill status={a.status} /></td>
                    <td className="px-4 py-3 text-right text-zinc-700">{a.videosUploaded}/4</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{fmtDate(a.createdAt)}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{a.submittedAt ? fmtDate(a.submittedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {total > filters.limit && (
          <div className="flex items-center justify-between mt-3 text-sm text-zinc-500">
            <span>
              Showing {filters.skip + 1}–{Math.min(filters.skip + filters.limit, total)} of {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                disabled={filters.skip <= 0}
                onClick={() => setFilters((f) => ({ ...f, skip: Math.max(0, f.skip - f.limit) }))}
                className="px-3 py-1.5 border border-zinc-200 rounded-lg disabled:opacity-40 hover:bg-zinc-100"
              >
                Previous
              </button>
              <button
                disabled={filters.skip + filters.limit >= total}
                onClick={() => setFilters((f) => ({ ...f, skip: f.skip + f.limit }))}
                className="px-3 py-1.5 border border-zinc-200 rounded-lg disabled:opacity-40 hover:bg-zinc-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <ApplicantDrawer applicantId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
};

export default ConciergeApplicantsScreen;
